// src/pages/Chatbot/ChatbotScripts.js
import React, { useState, useEffect } from "react";
import { useParams } from 'react-router-dom';
import axiosInstance from "../../utils/axiosInstance";
import { ChatScriptsURL, CHATBOT_UI } from "../../config";
import { useChatbots } from "../../contexts/ChatbotContext";
import { Box, Typography, Paper, TextField, Button, CircularProgress, Alert, Divider, useTheme } from "@mui/material";

export default function ChatbotScripts() {
   const theme = useTheme();
   const { activeChatbot } = useChatbots();
   const [error, setError] = useState(null);
   const [scriptFilename, setScriptFilename] = useState("");
   const [scriptData, setScriptData] = useState(null);
   const [loading, setLoading] = useState(true);
   const { chatbotId: paramsChatbotId } = useParams();

   useEffect(() => {
      if (activeChatbot) fetchData();
   }, [activeChatbot]);

   const fetchData = async () => {
      if (!activeChatbot) {
         setError("No active chatbot selected.");
         setLoading(false);
         return;
      }
      try {
         setLoading(true);
         const params = { chatbot_id: paramsChatbotId };
         const response = await axiosInstance.post(`/get-javascript`, {}, { params });
         const responseData = response.data.data;
         if (Object.keys(responseData).length > 0) setScriptData(responseData);
      } catch (error) {
         setError(error.response?.data?.message || "Failed to fetch script.");
      } finally {
         setLoading(false);
      }
   };

   useEffect(() => {
      if (scriptData?.chatbot_identity) {
         const dynamicScript = `<!-- Add in the <head> section -->
<script src="${ChatScriptsURL}/embed/klonit-chatbot.js"></script>
<script>
   function initializeChatbot() {
      if (window.KloneIT && typeof window.KloneIT.init === 'function') {
         const config = {CHATBOT_ID: '${scriptData.chatbot_identity}'};
         window.KloneIT.init(config);
      } else {setTimeout(initializeChatbot, 100);}
   }
   if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', initializeChatbot);
   } else {initializeChatbot();}
</script>`;
         setScriptFilename(dynamicScript);
      } else setScriptFilename("");
   }, [scriptData]);

   const handleCopyClick = () => {
      navigator.clipboard.writeText(scriptFilename)
         .then(() => alert("Script copied to clipboard!"))
         .catch((err) => console.error("Copy failed", err));
   };

   const chatbotLink = scriptData ? `${CHATBOT_UI}/?CHATBOT_ID=${scriptData.chatbot_identity}` : "#";

   if (loading) {
      return (
         <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", p: 4 }}>
            <CircularProgress />
            <Typography sx={{ ml: 2 }} color="text.primary">Loading script...</Typography>
         </Box>
      );
   }

   return (
      <Paper sx={{ p: 3, bgcolor: "background.paper" }}>
         <Typography variant="h5" color="text.primary" gutterBottom>
            Script for {activeChatbot?.chatbot_name || '...'}
         </Typography>
         <Divider sx={{ mb: 2 }} />
         <TextField
            value={scriptFilename}
            multiline
            fullWidth
            rows={10}
            InputProps={{ readOnly: true }}
            placeholder="Select a chatbot to generate its script."
            variant="outlined"
         />
         <Box sx={{ mt: 2, display: "inline-flex", flexWrap: "wrap", gap: 1 }}>
            <Button variant="contained" color="primary" onClick={handleCopyClick} disabled={!scriptFilename}>
               Copy Script
            </Button>
            <Button variant="outlined" href={chatbotLink} target="_blank" rel="noopener noreferrer">
               Open Chatbot
            </Button>
            <Button variant="outlined" href="wordpress-plugin">
               Download WordPress Plugin
            </Button>
         </Box>
         {error && <Alert severity="warning" sx={{ mt: 2 }}>{error}</Alert>}
      </Paper>
   );
}